'use client'

import 'mapbox-gl/dist/mapbox-gl.css'
import { useTranslations } from 'next-intl'
import Map, { Marker, Popup, type ViewStateChangeEvent } from 'react-map-gl'
import { Dealer, DealerMapSearchResult } from '@/types/dealers'
import { useRef, useState } from 'react'
import { Button } from '@/app/[locale]/(ui)/button'
import { DealerMapSearch } from '@/app/[locale]/(dealers)/dealer-map-search'
import { getViewportForPoints } from '@/lib/maps'
import { OSLO_LOCATION } from '@/lib/constants'
import { Location, Viewport } from '@/types/maps'
import { findNearestDealer } from '@/lib/dealers'

export const DealerMap = ({ dealers }: { dealers: Dealer[] }) => {
  const INITIAL_VIEWPORT = { ...OSLO_LOCATION, zoom: 10.3 }
  const t = useTranslations()
  const [dealer, setDealer] = useState<Dealer>()
  const [address, setAddress] = useState<Location & { label: string }>()
  const [viewport, setViewport] = useState<Viewport>(INITIAL_VIEWPORT)
  const mapRef = useRef<HTMLDivElement>(null)
  const accessToken = process.env.NEXT_PUBLIC_MAPBOX_TOKEN

  const onSearchChange = (result: DealerMapSearchResult) => {
    if (result.type === 'dealer') {
      const dealer = dealers.find((dealer) => dealer.name === result.dealer?.name)
      setDealer(dealer)
      setAddress(undefined)
      return onViewportChange([result])
    }
    const nearest = findNearestDealer(result, dealers)
    const nearestDealer = dealers.find((dealer) => dealer.name === nearest.dealer.name)!

    setDealer(nearestDealer)
    setAddress({ latitude: result.latitude, longitude: result.longitude, label: result.label })
    onViewportChange([result, nearestDealer.location])
  }

  const onViewportChange = (points: Location[]) => {
    if (!mapRef.current) return
    const { width, height } = mapRef.current.getBoundingClientRect()
    setViewport(getViewportForPoints(points, { width, height }))
  }

  const onMove = (event: ViewStateChangeEvent) => {
    setViewport({
      latitude: event.viewState.latitude,
      longitude: event.viewState.longitude,
      zoom: event.viewState.zoom,
    })
  }

  const onSearchClear = () => {
    setDealer(undefined)
    setAddress(undefined)
  }

  const onMarkerClick = (event: any, dealer: Dealer) => {
    event.originalEvent.stopPropagation()
    setAddress(undefined)
    setDealer(dealer)
  }

  return (
    <div className="space-y-6">
      <div className="max-w-xl px-6 sm:px-8">
        <DealerMapSearch dealers={dealers} onChange={onSearchChange} onClear={onSearchClear} />
      </div>
      <div ref={mapRef} className="h-[400px] w-auto overflow-hidden sm:mx-8 sm:rounded-xl md:h-[500px]">
        <Map
          {...viewport}
          mapboxAccessToken={accessToken}
          mapStyle="mapbox://styles/mapbox/standard"
          cooperativeGestures
          onMove={onMove}
        >
          {dealers.map((dealer) => (
            <Marker
              key={dealer.name}
              latitude={dealer.location.latitude}
              longitude={dealer.location.longitude}
              onClick={(event) => onMarkerClick(event, dealer)}
            >
              <i className="flex size-[20px] rounded-full border-2 border-black bg-isekk" />
            </Marker>
          ))}
          {address && (
            <Marker latitude={address.latitude} longitude={address.longitude} anchor="bottom">
              <i className="flex size-[20px] rounded-full border-2 border-black bg-orange-500" />
            </Marker>
          )}
          {dealer && (
            <Popup
              longitude={dealer.location.longitude}
              latitude={dealer.location.latitude}
              closeButton={false}
              closeOnClick={true}
              onClose={() => setDealer(undefined)}
              className="[&_.mapboxgl-popup-content]-border [&_.mapboxgl-popup-content]:rounded-xl [&_.mapboxgl-popup-content]:shadow"
            >
              <div className="z-10 space-y-2 rounded-md text-base">
                <div>
                  <strong className="text-base font-bold">{dealer.name}</strong>
                  <div className="text-sm">{dealer.address}</div>
                  <p className="text-sm">
                    {dealer.postalCode} {dealer.city}
                  </p>
                </div>
                <Button variant="secondary" className="w-full text-sm" href={dealer.mapsUrl} target="_blank">
                  {t('Open_in_map')}
                </Button>
              </div>
            </Popup>
          )}
        </Map>
      </div>
    </div>
  )
}
